import React from 'react';
import { Col, Row } from 'react-bootstrap';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from "recharts";
import moment from "moment";
import FormatedLine from '../../../components/FFDA/Reports/Detailed/FormatedLine';
import FormatedPercent from '../../../components/FFDA/Reports/Detailed/FormatedPercent';
import './DashboardDetailed.scss';


const monthFormat = "MM-D-YYYY ";
const monthFormat2 = "MMMM-YYYY ";

const DashboardDetailed = ({
    kpiData,
    financialSummaryData,
    reportSummaryData,
    isLoading 
}) => {
    let data = {};
    for (const [key, value] of Object.entries({...kpiData, ...financialSummaryData})) {
        data[key.toUpperCase()] = value;
    }

    const tRevenuYTD = data.OPERATING_INCOME_TOTAL_PRIOR;
    const tRevenu = data.OPERATING_INCOME_TOTAL;

    const dataGraph = [
        {
            name: "CURRENT YEAR - YTD",
            "Total Burial": data.TOTAL_BURIAL_YTD || 100,
            "Total Cremation": data.TOTAL_CREMATION_YTD || 10,
        },
        {
            name: "PRIOR YEAR - YTD",
            "Total Burial": data.TOTAL_BURIAL_PRIOR_YTD || 100,
            "Total Cremation": data.TOTAL_CREMATION_PRIOR_YTD || 10,
        },
    ];

    const COLORS = [
        '#7fa76e', //green
        '#77bfe2' //blue
    ];

    const dataGraph2 = [
        {
            name: "Total Burial",
            value: data.TOTAL_BURIAL_YTD || 100,
            key: "Total Burial"
        },
        {
            name: "Total Cremation",
            value: data.TOTAL_CREMATION_YTD || 10,
            key: "Total Cremation"
        },
    ];

    const dataGraph3 = [
        {
            name: "Total Burial Prior YTD",
            value: data.TOTAL_BURIAL_PRIOR_YTD || 100,
            key: "Total Burial"
        },
        {
            name: "Total Cremation Prior YTD",
            value: data.TOTAL_CREMATION_PRIOR_YTD || 10,
            key: "Total Cremation"
        },
    ];

    // ---------------- KPI ----------------
    // Number of calls
    let KPI_numberOfCallsPeriod = data.TOTAL_CALLS_BURIAL_MONTHLY + data.TOTAL_CALLS_CREMATION_MONTHLY
    let KPI_numberOfCallsYTD = data.TOTAL_CALLS_YTD
    let KPI_numberOfCallsPriorPeriod = data.TOTAL_BURIAL_PRIOR_YEAR_SAME_MONTH + data.TOTAL_CREMATION_PRIOR_YEAR_SAME_MONTH
    let KPI_numberOfCallsPriorYTD = data.TOTAL_CALLS_PRIOR_YTD
    let KPI_numberOfCallsVariance = KPI_numberOfCallsYTD - KPI_numberOfCallsPriorYTD
    let KPI_numberOfCallsPercent = KPI_numberOfCallsVariance / KPI_numberOfCallsPriorYTD * 100

    // Total Gross Revenue
    let KPI_totalGrossRevenuePeriod = data.OPERATING_INCOME_BURIAL_THIS_MONTH + data.OPERATING_INCOME_CREMATION_THIS_MONTH
    let KPI_totalGrossRevenueYTD = data.OPERATING_INCOME_TOTAL
    let KPI_totalGrossRevenuePriorPeriod = data.OPERATING_INCOME_TOTAL_PRIOR_MONTH
    let KPI_totalGrossRevenuePriorYTD = data.OPERATING_INCOME_TOTAL_PRIOR
    let KPI_totalGrossRevenueVariance = KPI_totalGrossRevenueYTD - KPI_totalGrossRevenuePriorYTD

    let KPI_totalGrossRevenuePercent = KPI_totalGrossRevenueVariance / KPI_totalGrossRevenuePriorYTD * 100
    if (KPI_totalGrossRevenueVariance > 0 && KPI_totalGrossRevenuePercent < 0) {
        KPI_totalGrossRevenuePercent *= -1
    }

    // Total Gross Profit
    let KPI_totalCostOfGoodSoldPeriod = data.GROSS_PROFIT_TOTAL_THIS_MONTH
    let KPI_totalCostOfGoodSoldYTD = data.GROSS_PROFIT_TOTAL
    let KPI_totalCostOfGoodSoldPriorPeriod = data.GROSS_PROFIT_TOTAL_PRIOR_MONTH
    let KPI_totalCostOfGoodSoldPriorYTD = data.GROSS_PROFIT_TOTAL_PRIOR
    let KPI_totalCostOfGoodSoldVariance = KPI_totalCostOfGoodSoldYTD - KPI_totalCostOfGoodSoldPriorYTD

    let KPI_totalCostOfGoodSoldPercent = (KPI_totalCostOfGoodSoldVariance) / KPI_totalCostOfGoodSoldPriorYTD * 100
    if (KPI_totalCostOfGoodSoldVariance > 0 && KPI_totalCostOfGoodSoldPercent < 0) {
        KPI_totalCostOfGoodSoldPercent *= -1
    }

    // Total Net Profit
    let KPI_totalNetProfilPeriod = data.MONTHLY_NET_PROFIT_LOSS
    let KPI_totalNetProfilYTD = data.NET_PROFIT_OR_LOSS_TOTAL
    let KPI_totalNetProfilPriorPeriod = data.NET_PROFIT_OR_LOSS_PRIOR_MONTH
    let KPI_totalNetProfilPriorYTD = data.PRIOR_NET_PROFIT_LOSS
    let KPI_totalNetProfilVariance = KPI_totalNetProfilYTD - KPI_totalNetProfilPriorYTD

    let KPI_totalNetProfilPercent = (KPI_totalNetProfilVariance) / KPI_totalNetProfilPriorYTD * 100
    if (KPI_totalNetProfilVariance > 0 && KPI_totalNetProfilPercent < 0) {
        KPI_totalNetProfilPercent *= -1
    }

    // ---------------- Call Volume ----------------
    // Total Burial
    let CallVolume_totalBurialMonthly = data.TOTAL_CALLS_BURIAL_MONTHLY
    let CallVolume_totalBurialYTD = data.TOTAL_BURIAL_YTD
    let CallVolume_totalBurialPriorYearMonth = data.TOTAL_BURIAL_PRIOR_YEAR_SAME_MONTH
    let CallVolume_totalBurialPriorYearYTD = data.TOTAL_BURIAL_PRIOR_YTD
    let CallVolume_totalBurialMonthlyVariance = CallVolume_totalBurialMonthly - CallVolume_totalBurialPriorYearMonth
    let CallVolume_totalBurialYTDVariance = CallVolume_totalBurialYTD - CallVolume_totalBurialPriorYearYTD

    let CallVolume_totalBurialMonthlyPercent = (CallVolume_totalBurialMonthlyVariance) / CallVolume_totalBurialPriorYearMonth * 100
    if (CallVolume_totalBurialMonthlyVariance > 0 && CallVolume_totalBurialMonthlyPercent < 0) {
        CallVolume_totalBurialMonthlyPercent *= -1
    }
    let CallVolume_totalBurialYTDPercent = (CallVolume_totalBurialYTDVariance) / CallVolume_totalBurialPriorYearYTD * 100
    if (CallVolume_totalBurialYTDVariance > 0 && CallVolume_totalBurialYTDPercent < 0) {
        CallVolume_totalBurialYTDPercent *= -1
    }

    // Total Cremation
    let CallVolume_totalCremationMonthly = data.TOTAL_CALLS_CREMATION_MONTHLY
    let CallVolume_totalCremationYTD = data.TOTAL_CREMATION_YTD
    let CallVolume_totalCremationPriorYearMonth = data.TOTAL_CREMATION_PRIOR_YEAR_SAME_MONTH
    let CallVolume_totalCremationPriorYearYTD = data.TOTAL_CREMATION_PRIOR_YTD
    let CallVolume_totalCremationMonthlyVariance = CallVolume_totalCremationMonthly - CallVolume_totalCremationPriorYearMonth
    let CallVolume_totalCremationYTDVariance = CallVolume_totalCremationYTD - CallVolume_totalCremationPriorYearYTD

    let CallVolume_totalCremationMonthlyPercent = (CallVolume_totalCremationMonthlyVariance) / CallVolume_totalCremationPriorYearMonth * 100
    if (CallVolume_totalCremationMonthlyVariance > 0 && CallVolume_totalCremationMonthlyPercent < 0) {
        CallVolume_totalCremationMonthlyPercent *= -1
    }
    let CallVolume_totalCremationYTDPercent = (CallVolume_totalCremationYTDVariance) / CallVolume_totalCremationPriorYearYTD * 100
    if (CallVolume_totalCremationYTDVariance > 0 && CallVolume_totalCremationYTDPercent < 0) {
        CallVolume_totalCremationYTDPercent *= -1
    }

    // Total Call Volume
    let CallVolume_totalCallVolumeMonthly = CallVolume_totalBurialMonthly + CallVolume_totalCremationMonthly
    let CallVolume_totalCallVolumeYTD = CallVolume_totalBurialYTD + CallVolume_totalCremationYTD
    let CallVolume_totalCallVolumePriorYearMonth = CallVolume_totalBurialPriorYearMonth + CallVolume_totalCremationPriorYearMonth
    let CallVolume_totalCallVolumePriorYearYTD = CallVolume_totalBurialPriorYearYTD + CallVolume_totalCremationPriorYearYTD
    let CallVolume_totalCallVolumeMonthlyVariance = CallVolume_totalCallVolumeMonthly - CallVolume_totalCallVolumePriorYearMonth
    let CallVolume_totalCallVolumeYTDVariance = CallVolume_totalCallVolumeYTD - CallVolume_totalCallVolumePriorYearYTD

    let CallVolume_totalCallVolumeMonthlyPercent = (CallVolume_totalCallVolumeMonthlyVariance) / CallVolume_totalCallVolumePriorYearMonth * 100
    if (CallVolume_totalCallVolumeMonthlyVariance > 0 && CallVolume_totalCallVolumeMonthlyPercent < 0) {
        CallVolume_totalCallVolumeMonthlyPercent *= -1
    }
    let CallVolume_totalCallVolumeYTDPercent = (CallVolume_totalCallVolumeYTDVariance) / CallVolume_totalCallVolumePriorYearYTD * 100
    if (CallVolume_totalCallVolumeYTDVariance > 0 && CallVolume_totalCallVolumeYTDPercent < 0) {
        CallVolume_totalCallVolumeYTDPercent *= -1
    }

    // ---------------- Sales Average ----------------
    // Total Revenue - Burial
    let SalesAverage_totalRevenueBurialThismonth = data.OPERATING_INCOME_BURIAL_THIS_MONTH
    let SalesAverage_totalRevenueBurialCurrentYearYTD = data.OPERATING_INCOME_BURIAL
    let SalesAverage_totalRevenueBurialPriorYearMonth = data.OPERATING_INCOME_BURIAL_PRIOR
    let SalesAverage_totalRevenueBurialPriorYTD = data.OPERATING_INCOME_BURIAL_PRIOR_YTD
    let SalesAverage_totalRevenueBurialMonthlyVariance = SalesAverage_totalRevenueBurialThismonth - SalesAverage_totalRevenueBurialPriorYearMonth
    let SalesAverage_totalRevenueBurialYTDVariance = SalesAverage_totalRevenueBurialCurrentYearYTD - SalesAverage_totalRevenueBurialPriorYTD

    let SalesAverage_totalRevenueBurialMonthlyPercent = (SalesAverage_totalRevenueBurialMonthlyVariance) / SalesAverage_totalRevenueBurialPriorYearMonth * 100
    if (SalesAverage_totalRevenueBurialMonthlyVariance > 0 && SalesAverage_totalRevenueBurialMonthlyPercent < 0) {
        SalesAverage_totalRevenueBurialMonthlyPercent *= -1
    }
    let SalesAverage_totalRevenueBurialYTDPercent = (SalesAverage_totalRevenueBurialYTDVariance) / SalesAverage_totalRevenueBurialPriorYTD * 100
    if (SalesAverage_totalRevenueBurialYTDVariance > 0 && SalesAverage_totalRevenueBurialYTDPercent < 0) {
        SalesAverage_totalRevenueBurialYTDPercent *= -1
    }

    // Total Revenue - Cremation
    let SalesAverage_totalRevenueCremationThismonth = data.OPERATING_INCOME_CREMATION_THIS_MONTH
    let SalesAverage_totalRevenueCremationCurrentYearYTD = data.OPERATING_INCOME_CREMATION
    let SalesAverage_totalRevenueCremationPriorYearMonth = data.OPERATING_INCOME_CREMATION_PRIOR
    let SalesAverage_totalRevenueCremationPriorYTD = data.OPERATING_INCOME_CREMATION_PRIOR_YTD
    let SalesAverage_totalRevenueCremationMonthlyVariance = SalesAverage_totalRevenueCremationThismonth - SalesAverage_totalRevenueCremationPriorYearMonth
    let SalesAverage_totalRevenueCremationYTDVariance = SalesAverage_totalRevenueCremationCurrentYearYTD - SalesAverage_totalRevenueCremationPriorYTD

    let SalesAverage_totalRevenueCremationMonthlyPercent = (SalesAverage_totalRevenueCremationMonthlyVariance) / SalesAverage_totalRevenueCremationPriorYearMonth * 100
    if (SalesAverage_totalRevenueCremationMonthlyVariance > 0 && SalesAverage_totalRevenueBurialMonthlyPercent < 0) {
        SalesAverage_totalRevenueCremationMonthlyPercent *= -1
    }
    let SalesAverage_totalRevenueCremationYTDPercent = (SalesAverage_totalRevenueCremationYTDVariance) / SalesAverage_totalRevenueCremationPriorYTD * 100
    if (SalesAverage_totalRevenueCremationYTDVariance > 0 && SalesAverage_totalRevenueCremationYTDPercent < 0) {
        SalesAverage_totalRevenueCremationYTDPercent *= -1
    }

    // Total Revenue
    let SalesAverage_totalRevenueThismonth = SalesAverage_totalRevenueBurialThismonth + SalesAverage_totalRevenueCremationThismonth
    let SalesAverage_totalRevenueCurrentYearYTD = SalesAverage_totalRevenueBurialCurrentYearYTD + SalesAverage_totalRevenueCremationCurrentYearYTD
    let SalesAverage_totalRevenuePriorYearMonth = SalesAverage_totalRevenueBurialPriorYearMonth + SalesAverage_totalRevenueCremationPriorYearMonth
    let SalesAverage_totalRevenuePriorYTD = SalesAverage_totalRevenueBurialPriorYTD + SalesAverage_totalRevenueCremationPriorYTD
    let SalesAverage_totalRevenueMonthlyVariance = SalesAverage_totalRevenueThismonth - SalesAverage_totalRevenuePriorYearMonth
    let SalesAverage_totalRevenueYTDVariance = SalesAverage_totalRevenueCurrentYearYTD - SalesAverage_totalRevenuePriorYTD

    let SalesAverage_totalRevenueMonthlyPercent = (SalesAverage_totalRevenueMonthlyVariance) / SalesAverage_totalRevenuePriorYearMonth * 100
    if (SalesAverage_totalRevenueMonthlyVariance > 0 && SalesAverage_totalRevenueBurialMonthlyPercent < 0) {
        SalesAverage_totalRevenueMonthlyPercent *= -1
    }
    let SalesAverage_totalRevenueYTDPercent = (SalesAverage_totalRevenueYTDVariance) / SalesAverage_totalRevenuePriorYTD * 100
    if (SalesAverage_totalRevenueYTDVariance > 0 && SalesAverage_totalRevenueYTDPercent < 0) {
        SalesAverage_totalRevenueYTDPercent *= -1
    }

    // Number of Calls
    let SalesAverage_numberOfCallsYTD = data.TOTAL_CALLS_YTD
    let SalesAverage_numberOfCallsPriorYTD = data.TOTAL_CALLS_PRIOR_YTD
    let SalesAverage_numberOfCallsYTDVariance = SalesAverage_numberOfCallsYTD - SalesAverage_numberOfCallsPriorYTD

    // Number of Calls - Burial
    let SalesAverage_numberOfCallsBurialYTD = data.TOTAL_BURIAL_YTD
    let SalesAverage_numberOfCallsPriorBurialYTD = data.TOTAL_BURIAL_PRIOR_YTD
    let SalesAverage_numberOfCallsBurialYTDVariance = SalesAverage_numberOfCallsBurialYTD - SalesAverage_numberOfCallsPriorBurialYTD

    let SalesAverage_numberOfCallsBurialYTDPercent = SalesAverage_numberOfCallsBurialYTD / SalesAverage_numberOfCallsYTD * 100
    let SalesAverage_numberOfCallsBurialPriorYTDPercent = SalesAverage_numberOfCallsPriorBurialYTD / SalesAverage_numberOfCallsPriorYTD * 100

    // Average Selling Price of a Burial
    let SalesAverage_averageSellingPriceBurialYTD = data.AVG_OPERATING_INCOME_BURIAL
    let SalesAverage_averageSellingPriceBurialPriorYTD = data.OPERATING_INCOME_BURIAL_PRIOR_YTD / data.TOTAL_BURIAL_PRIOR_YTD
    let SalesAverage_averageSellingPriceBurialYTDVariance = SalesAverage_averageSellingPriceBurialYTD - SalesAverage_averageSellingPriceBurialPriorYTD

    let SalesAverage_averageSellingPriceBurialYTDPercent = (SalesAverage_averageSellingPriceBurialYTDVariance) / SalesAverage_averageSellingPriceBurialPriorYTD * 100
    if (SalesAverage_averageSellingPriceBurialYTDVariance > 0 && SalesAverage_averageSellingPriceBurialYTDPercent < 0) {
        SalesAverage_averageSellingPriceBurialYTDPercent *= -1
    }

    // Number of Calls - Cremation
    let SalesAverage_numberOfCallsCremationYTD = data.TOTAL_CREMATION_YTD
    let SalesAverage_numberOfCallsPriorCremationYTD = data.TOTAL_CREMATION_PRIOR_YTD
    let SalesAverage_numberOfCallsCremationYTDVariance = SalesAverage_numberOfCallsCremationYTD - SalesAverage_numberOfCallsPriorCremationYTD

    let SalesAverage_numberOfCallsCremationYTDPercent = SalesAverage_numberOfCallsCremationYTD / SalesAverage_numberOfCallsYTD * 100
    let SalesAverage_numberOfCallsCremationPriorYTDPercent = SalesAverage_numberOfCallsPriorCremationYTD / SalesAverage_numberOfCallsPriorYTD * 100

    // Average Selling Price of a Cremation
    let SalesAverage_averageSellingPriceCremationYTD = data.AVG_OPERATING_INCOME_CREMATION
    let SalesAverage_averageSellingPriceCremationPriorYTD = data.OPERATING_INCOME_CREMATION_PRIOR_YTD / data.TOTAL_CREMATION_PRIOR_YTD
    let SalesAverage_averageSellingPriceCremationYTDVariance = SalesAverage_averageSellingPriceCremationYTD - SalesAverage_averageSellingPriceCremationPriorYTD

    let SalesAverage_averageSellingPriceCremationYTDPercent = (SalesAverage_averageSellingPriceCremationYTDVariance) / SalesAverage_averageSellingPriceCremationPriorYTD * 100
    if (SalesAverage_averageSellingPriceCremationYTDVariance > 0 && SalesAverage_averageSellingPriceCremationYTDPercent < 0) {
        SalesAverage_averageSellingPriceCremationYTDPercent *= -1
    }

    // Revenue Evolution
    let RevenueEvolution_VolumeEffect = (KPI_numberOfCallsYTD - KPI_numberOfCallsPriorYTD) * (SalesAverage_totalRevenuePriorYTD / KPI_numberOfCallsPriorYTD)
    let RevenueEvolution_PriceEffect = ((SalesAverage_totalRevenueCurrentYearYTD / KPI_numberOfCallsYTD) - (SalesAverage_totalRevenuePriorYTD / KPI_numberOfCallsPriorYTD)) * KPI_numberOfCallsYTD
    // ((Current YTD revenue / call CYTD) - (Prior YTD revenue / call PYTD)) x CY call

    // ---------------- Gross Profit Analysis ----------------
    // Gross Profit - Burial
    let GrossProfitAnalysis_grossProfitBurialYTD = data.GROSS_PROFIT_BURIAL
    let GrossProfitAnalysis_grossProfitBurialPriorYTD = data.GROSS_PROFIT_BURIAL_PRIOR_YTD
    let GrossProfitAnalysis_grossProfitBurialYTDVariance = GrossProfitAnalysis_grossProfitBurialYTD - GrossProfitAnalysis_grossProfitBurialPriorYTD

    let GrossProfitAnalysis_grossProfitBurialYTDPercent = (GrossProfitAnalysis_grossProfitBurialYTDVariance) / GrossProfitAnalysis_grossProfitBurialPriorYTD * 100
    if (GrossProfitAnalysis_grossProfitBurialYTDVariance > 0 && GrossProfitAnalysis_grossProfitBurialYTDPercent < 0) {
        GrossProfitAnalysis_grossProfitBurialYTDPercent *= -1
    }

    let GrossProfitAnalysis_asBurialRevenueYTDPercent = (GrossProfitAnalysis_grossProfitBurialYTD) / data.OPERATING_INCOME_BURIAL * 100
    let GrossProfitAnalysis_asBurialRevenuePriorYTDPercent = (GrossProfitAnalysis_grossProfitBurialPriorYTD) / data.OPERATING_INCOME_BURIAL_PRIOR_YTD * 100

    // Gross Profit - Cremation
    let GrossProfitAnalysis_grossProfitCremationYTD = data.GROSS_PROFIT_CREMATION
    let GrossProfitAnalysis_grossProfitCremationPriorYTD = data.GROSS_PROFIT_CREMATION_PRIOR_YTD
    let GrossProfitAnalysis_grossProfitCremationYTDVariance = GrossProfitAnalysis_grossProfitCremationYTD - GrossProfitAnalysis_grossProfitCremationPriorYTD

    let GrossProfitAnalysis_grossProfitCremationYTDPercent = (GrossProfitAnalysis_grossProfitCremationYTDVariance) / GrossProfitAnalysis_grossProfitCremationPriorYTD * 100
    if (GrossProfitAnalysis_grossProfitCremationYTDVariance > 0 && GrossProfitAnalysis_grossProfitCremationYTDPercent < 0) {
        GrossProfitAnalysis_grossProfitCremationYTDPercent *= -1
    }

    let GrossProfitAnalysis_asCremationRevenueYTDPercent = (GrossProfitAnalysis_grossProfitCremationYTD) / data.OPERATING_INCOME_CREMATION * 100
    let GrossProfitAnalysis_asCremationRevenuePriorYTDPercent = (GrossProfitAnalysis_grossProfitCremationPriorYTD) / data.OPERATING_INCOME_CREMATION_PRIOR_YTD * 100

    // TOTAL Gross Profit
    let GrossProfitAnalysis_totalGrossProfitYTD = data.GROSS_PROFIT_TOTAL
    let GrossProfitAnalysis_totalGrossProfitPriorYTD = data.GROSS_PROFIT_TOTAL_PRIOR
    let GrossProfitAnalysis_totalGrossProfitYTDVariance = GrossProfitAnalysis_totalGrossProfitYTD - GrossProfitAnalysis_totalGrossProfitPriorYTD

    let GrossProfitAnalysis_totalGrossProfitYTDPercent = (GrossProfitAnalysis_totalGrossProfitYTDVariance) / GrossProfitAnalysis_totalGrossProfitPriorYTD * 100
    if (GrossProfitAnalysis_totalGrossProfitYTDVariance > 0 && GrossProfitAnalysis_totalGrossProfitYTDPercent < 0) {
        GrossProfitAnalysis_totalGrossProfitYTDPercent *= -1
    }

    return (
        <Row className="reportsApp">
            <Col md={12} className="periodTitles">
                <div className="content">
                    <div className="oneItem">Month : <span className="smallTitle">{moment(String(data.END_REPORT_CYCLE)).format(monthFormat2)}</span></div>
                    <div className="oneItem d-flex">
                        <div className='smallTitlePrefix'>Year to date :</div>
                        <div className='smallTitlePrefix'>
                            From <span className="smallTitle">{moment(String(data.START_REPORT_CYCLE)).format(monthFormat)}</span>
                        </div>
                        <div className='smallTitlePrefix'>
                            To <span className="smallTitle">{moment(String(data.END_REPORT_CYCLE)).endOf('month').format(monthFormat)}</span>
                        </div>
                    </div>
                </div>
            </Col>
            <Col md={12} className="bigTitle">
                <div className="content">KPIs</div>
            </Col>
            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4} className="oneTitleBlock">
                        <div className="empty"></div>
                    </Col>
                    <Col md={7}>
                        <Row>
                            <Col md={2} className="oneTitleBlock faded">
                                <div className="content volumeTitle">Period</div>
                            </Col>
                            <Col md={3} className="oneTitleBlock">
                                <div className="content volumeTitle">YTD</div>
                            </Col>
                            <Col md={2} className="oneTitleBlock faded">
                                <div className="content volumeTitle">Prior Period</div>
                            </Col>
                            <Col md={3} className="oneTitleBlock">
                                <div className="content volumeTitle">Prior YTD</div>
                            </Col>
                            <Col md={2} className="oneTitleBlock grey">
                                <div className="content volumeTitle">Variance<br />CYTD vs.PY</div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={1} className="oneTitleBlock grey">
                        <div className="content volumeTitle">% YTD <br />CY vs. PY</div>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi grey">
                        <div className="content subtitle">Number of calls</div>
                    </Col>
                    <Col md={7}>
                        <Row>
                            <Col md={2} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine content={KPI_numberOfCallsPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine content={KPI_numberOfCallsYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine content={KPI_numberOfCallsPriorPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine content={KPI_numberOfCallsPriorYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine content={KPI_numberOfCallsVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={1} className="oneContentKpi grey">
                        <div className="content">
                            <FormatedPercent content={KPI_numberOfCallsPercent} />
                        </div>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi white">
                        <div className="content subtitle">Total Revenue</div>
                    </Col>
                    <Col md={7}>
                        <Row>
                            <Col md={2} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalGrossRevenuePeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalGrossRevenueYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalGrossRevenuePriorPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalGrossRevenuePriorYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalGrossRevenueVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={1} className="oneContentKpi white">
                        <div className="content">
                            <FormatedPercent content={KPI_totalGrossRevenuePercent} />
                        </div>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi grey">
                        <div className="content subtitle">Total Gross Profit</div>
                    </Col>
                    <Col md={7}>
                        <Row>
                            <Col md={2} className="oneContentKpi lightYellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalCostOfGoodSoldPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi lightYellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalCostOfGoodSoldYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi lightYellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalCostOfGoodSoldPriorPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi lightYellow">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalCostOfGoodSoldPriorYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalCostOfGoodSoldVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={1} className="oneContentKpi grey">
                        <div className="content">
                            <FormatedPercent content={KPI_totalCostOfGoodSoldPercent} />
                        </div>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi white bigTotal">
                        <div className="content subtitle">Net Profit or Loss</div>
                    </Col>
                    <Col md={7}>
                        <Row>
                            <Col md={2} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalNetProfilPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalNetProfilYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalNetProfilPriorPeriod} />
                                </div>
                            </Col>
                            <Col md={3} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalNetProfilPriorYTD} />
                                </div>
                            </Col>
                            <Col md={2} className="oneContentKpi white bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={KPI_totalNetProfilVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={1} className="oneContentKpi white bigTotal">
                        <div className="content">
                            <FormatedPercent content={KPI_totalNetProfilPercent} />
                        </div>
                    </Col>
                </Row>
            </Col>
            <Col md={12} className="bigTitle">
                <div className="content">Call Volume</div>
            </Col>
            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4} className="oneTitleBlock">
                        <div className="empty"></div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock blue faded">
                                        <div className="content volumeTitle">This month</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock lightBlue volumeTitle">
                                        <div className="content volumeTitle">Current Year<br />- YTD</div>
                                    </Col>
                                </Row>
                            </Col>

                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock ligthBlue volumeTitle faded">
                                        <div className="content volumeTitle">Prior Year - <br />same month</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock lightBlue volumeTitle">
                                        <div className="content volumeTitle">Prior YTD</div>
                                    </Col>
                                </Row>
                            </Col>

                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock lightBlue volumeTitle grey">
                                        <div className="content volumeTitle">% Period <br />CY vs. PY</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock lightBlue volumeTitle grey">
                                        <div className="content volumeTitle">% YTD <br />CY vs. PY</div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi green">
                        <div className="content subtitle">Total Burial</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalBurialMonthly} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalBurialYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalBurialPriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalBurialPriorYearYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalBurialMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalBurialYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi blue">
                        <div className="content subtitle">Total Cremation</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCremationMonthly} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCremationYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCremationPriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCremationPriorYearYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalCremationMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalCremationYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="rowContentBlock bold">
                    <Col md={4} className="oneContentKpi white bigTotal">
                        <div className="content subtitle">Total Call Volume</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCallVolumeMonthly} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCallVolumeYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCallVolumePriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine content={CallVolume_totalCallVolumePriorYearYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalCallVolumeMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedPercent content={CallVolume_totalCallVolumeYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
            <Col md={12}>
                <Row>
                    <Col md={5} className="chart column">
                        <ResponsiveContainer height='100%' width='100%' aspect={3}>
                            <BarChart
                                // width={800}
                                // height={300}
                                data={dataGraph}
                                margin={{
                                    top: 5,
                                    bottom: 5
                                }}
                                barCategoryGap="30%"
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="Total Burial" fill={COLORS[0]} />
                                <Bar dataKey="Total Cremation" fill={COLORS[1]} />
                            </BarChart>
                        </ResponsiveContainer>

                    </Col>
                    <Col md={7} className="chart pie">
                        <Row>
                            <Col md={6}>
                                <ResponsiveContainer height='100%' width='100%' aspect={1}>
                                    <PieChart>
                                        <Legend />
                                        <Pie data={dataGraph2} dataKey="value" nameKey="name" cx="50%" cy="50%" fill="#8884d8" label>
                                            {dataGraph2.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Col>
                            <Col md={6}>
                                <ResponsiveContainer height='100%' width='100%' aspect={1}>
                                    <PieChart>
                                        <Legend />
                                        <Pie data={dataGraph3} dataKey="value" nameKey="name" cx="50%" cy="50%" fill="#8884d8" label>
                                            {dataGraph3.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
            <Col md={12} className="bigTitle">
                <div className="content">Sales Average</div>
            </Col>
            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4} className="oneTitleBlock">
                        <div className="empty"></div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock faded">
                                        <div className="content volumeTitle">This month</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock">
                                        <div className="content volumeTitle">Current Year - YTD</div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock faded">
                                        <div className="content volumeTitle">Prior Year – same month</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock">
                                        <div className="content volumeTitle">Prior YTD</div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneTitleBlock grey">
                                        <div className="content volumeTitle">% Period <br />CY vs. PY</div>
                                    </Col>
                                    <Col md={6} className="oneTitleBlock grey">
                                        <div className="content volumeTitle">% YTD <br />CY vs. PY</div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    <Col md={4} className="oneContentKpi green">
                        <div className="content">Total Revenue - Burial</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueBurialThismonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueBurialCurrentYearYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueBurialPriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueBurialPriorYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueBurialMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightGreen">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueBurialYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={4} className="oneContentKpi blue">
                        <div className="content">Total Revenue - Cremation</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueCremationThismonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueCremationCurrentYearYTD} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueCremationPriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueCremationPriorYTD} />
                                        </div>
                                    </Col>

                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueCremationMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi lightBlue">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueCremationYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={4} className="oneContentKpi bigTotal">
                        <div className="content">Total Revenue</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueThismonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenueCurrentYearYTD} />
                                        </div>
                                    </Col>

                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenuePriorYearMonth} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedLine type="money" content={SalesAverage_totalRevenuePriorYTD} />
                                        </div>
                                    </Col>

                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueMonthlyPercent} />
                                        </div>
                                    </Col>
                                    <Col md={6} className="oneContentKpi white bigTotal">
                                        <div className="content">
                                            <FormatedPercent content={SalesAverage_totalRevenueYTDPercent} />
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
            <Col md={12} className="contentBlock part">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4}>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={3} className="oneTitleBlock smallTitle">
                                <div className="content volumeTitle">YTD</div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneTitleBlock smallTitle">
                                <div className="content volumeTitle">Prior Year</div>
                            </Col>

                            <Col md={{ span: 3, offset: 2 }} className="oneTitleBlock smallTitle">
                                <div className="content volumeTitle">Variance</div>
                            </Col>
                        </Row>
                    </Col>


                </Row>
                <Row className="rowContentBlock">
                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi black mbotton">
                        <div className="content">Number of Calls</div>
                    </Col>
                    <Col md={8} className="mbotton">
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi orange">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsPriorYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 2 }} className="oneContentKpi black">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsYTDVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi green mbotton">
                        <div className="content">Number of Calls - Burial</div>
                    </Col>
                    <Col md={8} className="mbotton">
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsBurialYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_numberOfCallsBurialYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi orange">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsPriorBurialYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_numberOfCallsBurialPriorYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi black">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsBurialYTDVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi green mbotton">
                        <div className="content">Average Selling Price of a Burial</div>
                    </Col>
                    <Col md={8} className="mbotton">
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={SalesAverage_averageSellingPriceBurialYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi orange">
                                <div className="content">
                                    <FormatedLine type="money" content={SalesAverage_averageSellingPriceBurialPriorYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_averageSellingPriceBurialYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 4, offset: 0 }} className="oneContentKpi white">
                                <div className="content">Evolution vs. PY%</div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi blue mbotton">
                        <div className="content">Number of Calls - Cremation</div>
                    </Col>
                    <Col md={8} className="mbotton">
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsCremationYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_numberOfCallsCremationYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi orange">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsPriorCremationYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_numberOfCallsCremationPriorYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi black">
                                <div className="content">
                                    <FormatedLine content={SalesAverage_numberOfCallsCremationYTDVariance} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi blue mbotton">
                        <div className="content">Average Selling Price of a Cremation</div>
                    </Col>
                    <Col md={8} className="mbotton">
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow">
                                <div className="content">
                                    <FormatedLine type="money" content={SalesAverage_averageSellingPriceCremationYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi orange">
                                <div className="content">
                                    <FormatedLine type="money" content={SalesAverage_averageSellingPriceCremationPriorYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 1, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={SalesAverage_averageSellingPriceCremationYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 4, offset: 0 }} className="oneContentKpi white">
                                <div className="content">Evolution vs. PY%</div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
            <Col md={12} className="bigTitle">
                <div className="content">Gross Profit Analysis</div>
            </Col>
            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4} className="oneTitleBlock">
                        <div className="empty"></div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={3} className="oneTitleBlock">
                                <div className="content">Current Year - YTD</div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneTitleBlock">
                                <div className="content">Prior YTD</div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneTitleBlock">
                                <div className="content">% YTD <br />CY vs. PY</div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="rowContentBlock">
                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi green">
                        <div className="content">Gross Profit - Burial</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_grossProfitBurialYTD} />
                                </div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_grossProfitBurialPriorYTD} />
                                </div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_grossProfitBurialYTDPercent} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi white" style={{ textAlign: 'center' }}>
                        <div className="content">as % of Burial revenue</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_asBurialRevenueYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_asBurialRevenuePriorYTDPercent} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={{ span: 4, offset: 0 }} className="oneContentKpi blue">
                        <div className="content">Gross Profit - Cremation</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_grossProfitCremationYTD} />
                                </div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_grossProfitCremationPriorYTD} />
                                </div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_grossProfitCremationYTDPercent} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi white" style={{ textAlign: 'center' }}>
                        <div className="content">as % of Cremation revenue</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_asCremationRevenueYTDPercent} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_asCremationRevenuePriorYTDPercent} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={{ span: 4, offset: 0 }} className="oneContentKpi black bigTotal">
                        <div className="content">TOTAL Gross Profit</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_totalGrossProfitYTD} />
                                </div>
                            </Col>
                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi yellow bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={GrossProfitAnalysis_totalGrossProfitPriorYTD} />
                                </div>
                            </Col>

                            <Col md={{ span: 3, offset: 1 }} className="oneContentKpi white bigTotal">
                                <div className="content">
                                    <FormatedPercent content={GrossProfitAnalysis_totalGrossProfitYTDPercent} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>


            <Col md={12} className="bigTitle">
                <div className="content">Revenue Evolution YTD vs. Prior YTD</div>
            </Col>

            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock">
                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi grey">
                        <div className="content">Volume effect</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi black">
                                <div className="content">
                                    <FormatedLine type="money" content={RevenueEvolution_VolumeEffect} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi white" style={{ textAlign: 'center' }}>
                        <div className="content">Price effect</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 3, offset: 0 }} className="oneContentKpi black">
                                <div className="content">
                                    <FormatedLine type="money" content={RevenueEvolution_PriceEffect} />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>

            <Col md={12} className="bigTitle">
                <div className="content">Operating Expenses</div>
            </Col>
            <Col md={12} className="contentBlock">
                <Row className="rowContentBlock titlesBlock">
                    <Col md={4} className="oneTitleBlock">
                        <div className="empty"></div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={2} className="oneTitleBlock">
                                <div className="content volumeTitle">This month</div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneTitleBlock">
                                <div className="content volumeTitle">Current Year - YTD</div>
                            </Col>

                            <Col md={{ span: 2, offset: 0 }} className="oneTitleBlock">
                                <div className="content volumeTitle">% of Revenue</div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneTitleBlock">
                                <div className="content volumeTitle">Prior Year - same period</div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneTitleBlock">
                                <div className="content volumeTitle">Prior YTD</div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneTitleBlock">
                                <div className="content volumeTitle">% of Revenue</div>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row className="rowContentBlock">
                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Facilities</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_FACILITY} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_FACILITY} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_FACILITY / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_FACILITY} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_FACILITY} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_FACILITY / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>


                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Automotive</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_AUTOMOTIVE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_AUTOMOTIVE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_AUTOMOTIVE / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_AUTOMOTIVE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_AUTOMOTIVE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_AUTOMOTIVE / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Personnel</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_PERSONNEL} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_PERSONNEL} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_PERSONNEL / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_PERSONNEL} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_PERSONNEL} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_PERSONNEL / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>


                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Supplies</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_SUPPLIES} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_SUPPLIES} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_SUPPLIES / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_SUPPLIES} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_SUPPLIES} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_SUPPLIES / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>



                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Business service</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_BUSINESS_SERVICE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_BUSINESS_SERVICE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_BUSINESS_SERVICE / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_BUSINESS_SERVICE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_BUSINESS_SERVICE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_BUSINESS_SERVICE / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Promotion</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_PROMOTION} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_PROMOTION} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_PROMOTION / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_PROMOTION} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_PROMOTION} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_PROMOTION / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>


                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">After Sale</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_AFTER_SALE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_AFTER_SALE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_AFTER_SALE / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightGreen">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_AFTER_SALE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi blue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_AFTER_SALE} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_AFTER_SALE / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi sGrey">
                        <div className="content">Miscellaneous</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.THIS_MONTH_MISCELLANEOUS} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.CURRENT_YTD_MISCELLANEOUS} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.CURRENT_YTD_MISCELLANEOUS / tRevenu) * 100} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YEAR_MONTH_MISCELLANEOUS} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue">
                                <div className="content">
                                    <FormatedLine type="money" content={data.PRIOR_YTD_MISCELLANEOUS} />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={(data.PRIOR_YTD_MISCELLANEOUS / tRevenuYTD) * 100} />
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/*  *** ROW *** */}
                    <Col md={4} className="oneContentKpi black bigTotal">
                        <div className="content">Operating expenses</div>
                    </Col>
                    <Col md={8}>
                        <Row>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue bold bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={
                                        Math.round(
                                        data.THIS_MONTH_FACILITY +
                                        data.THIS_MONTH_AUTOMOTIVE +
                                        data.THIS_MONTH_PERSONNEL +
                                        data.THIS_MONTH_SUPPLIES +
                                        data.THIS_MONTH_BUSINESS_SERVICE +
                                        data.THIS_MONTH_PROMOTION +
                                        data.THIS_MONTH_AFTER_SALE +
                                        data.THIS_MONTH_MISCELLANEOUS)
                                    } />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue bold bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={
                                        Math.round(
                                        data.CURRENT_YTD_FACILITY +
                                        data.CURRENT_YTD_AUTOMOTIVE +
                                        data.CURRENT_YTD_PERSONNEL +
                                        data.CURRENT_YTD_SUPPLIES +
                                        data.CURRENT_YTD_BUSINESS_SERVICE +
                                        data.CURRENT_YTD_PROMOTION +
                                        data.CURRENT_YTD_AFTER_SALE +
                                        data.CURRENT_YTD_MISCELLANEOUS)
                                    } />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white bold bigTotal">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={
                                        ((data.CURRENT_YTD_FACILITY / tRevenu) +
                                            (data.CURRENT_YTD_AUTOMOTIVE / tRevenu) +
                                            (data.CURRENT_YTD_PERSONNEL / tRevenu) +
                                            (data.CURRENT_YTD_SUPPLIES / tRevenu) +
                                            (data.CURRENT_YTD_BUSINESS_SERVICE / tRevenu) +
                                            (data.CURRENT_YTD_PROMOTION / tRevenu) +
                                            (data.CURRENT_YTD_AFTER_SALE / tRevenu) +
                                            (data.CURRENT_YTD_MISCELLANEOUS / tRevenu)) * 100
                                    } />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi grey bold bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={
                                        data.PRIOR_YEAR_MONTH_FACILITY +
                                        data.PRIOR_YEAR_MONTH_AUTOMOTIVE +
                                        data.PRIOR_YEAR_MONTH_PERSONNEL +
                                        data.PRIOR_YEAR_MONTH_SUPPLIES +
                                        data.PRIOR_YEAR_MONTH_BUSINESS_SERVICE +
                                        data.PRIOR_YEAR_MONTH_PROMOTION +
                                        data.PRIOR_YEAR_MONTH_AFTER_SALE +
                                        data.PRIOR_YEAR_MONTH_MISCELLANEOUS
                                    } />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi lightBlue bold bigTotal">
                                <div className="content">
                                    <FormatedLine type="money" content={
                                        data.PRIOR_YTD_FACILITY +
                                        data.PRIOR_YTD_AUTOMOTIVE +
                                        data.PRIOR_YTD_PERSONNEL +
                                        data.PRIOR_YTD_SUPPLIES +
                                        data.PRIOR_YTD_BUSINESS_SERVICE +
                                        data.PRIOR_YTD_PROMOTION +
                                        data.PRIOR_YTD_AFTER_SALE +
                                        data.PRIOR_YTD_MISCELLANEOUS
                                    } />
                                </div>
                            </Col>
                            <Col md={{ span: 2, offset: 0 }} className="oneContentKpi white bold bigTotal">
                                <div className="content">
                                    <FormatedPercent fixed={1} content={
                                        ((data.PRIOR_YTD_FACILITY / tRevenuYTD) +
                                            (data.PRIOR_YTD_AUTOMOTIVE / tRevenuYTD) +
                                            (data.PRIOR_YTD_PERSONNEL / tRevenuYTD) +
                                            (data.PRIOR_YTD_SUPPLIES / tRevenuYTD) +
                                            (data.PRIOR_YTD_BUSINESS_SERVICE / tRevenuYTD) +
                                            (data.PRIOR_YTD_PROMOTION / tRevenuYTD) +
                                            (data.PRIOR_YTD_AFTER_SALE / tRevenuYTD) +
                                            (data.PRIOR_YTD_MISCELLANEOUS / tRevenuYTD)) * 100
                                    } />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Col>
        </Row >
    )
}

export default DashboardDetailed;
